import { useEffect, useMemo, useRef } from 'react'
import useFeatureToggle from '@/hooks/useFeatureToggle'
import { usePocOperationalStatus } from '@/proxy/seller/hooks'
import { useZendeskMessagingJwt } from '@/proxy/zendesk/hooks'
import { FeatureToggle } from '@/resources/enums/featureToggle'
import { getSupportChatProvider, SUPPORT_CHAT_SPLIT_NAME } from './config'
import { isSupportChatEnabledForSeller } from './eligibility'
import { useSupportChatPrintListeners } from './printListeners'
import {
  clearSupportChatProvider,
  registerSupportChatProvider,
} from './supportChatController'
import { createSprinklrProvider } from './providers/sprinklr/provider'
import { createZendeskProvider } from './providers/zendesk/provider'
import { supportChatLogger } from './logger'
import { SupportChatSeller, SupportChatUser } from './types'

const logSupportChatDebug = (
  message: string,
  details: Record<string, unknown>
) => {
  if (process.env.ENV !== 'production') {
    console.info(`[SupportChat] ${message}`, details)
  }
}

const SupportChat = () => {
  const featureToggle = useFeatureToggle({
    splitName: SUPPORT_CHAT_SPLIT_NAME,
    includeConfig: true,
    includeLoadingState: true,
  })
  const { data } = usePocOperationalStatus()
  const { mutateAsync: getZendeskJwt } = useZendeskMessagingJwt()
  const seller: SupportChatSeller = useMemo(
    () => data?.seller ?? {},
    [data?.seller]
  )
  const user: SupportChatUser = useMemo(() => data?.user ?? {}, [data?.user])
  const loadedProviderSignatureRef = useRef<string | null>(null)

  useSupportChatPrintListeners()
  const isFeatureLoading = featureToggle === FeatureToggle.LOADING
  const featureToggleConfig =
    typeof featureToggle === 'object' ? featureToggle.config : null
  const featureToggleTreatment =
    typeof featureToggle === 'object' ? featureToggle.treatment : featureToggle
  const providerName = getSupportChatProvider({
    treatment: featureToggleTreatment,
    config: featureToggleConfig,
  })
  const isSellerEligible = isSupportChatEnabledForSeller(seller.status)
  const providerSignature = `${providerName}:${String(seller.id ?? '')}:${String(
    seller.status ?? ''
  )}:${String(user.chatHashCode ?? '')}`

  const provider = useMemo(
    () =>
      providerName === 'zendesk'
        ? createZendeskProvider({
            seller,
            getJwt: () => getZendeskJwt(),
          })
        : createSprinklrProvider(seller, user),
    [providerName, seller, user, getZendeskJwt]
  )

  useEffect(() => {
    logSupportChatDebug('state', {
      featureToggle,
      isFeatureLoading,
      providerName,
      isSellerEligible,
      sellerStatus: seller.status,
      sellerId: seller.id,
    })

    if (isFeatureLoading || !isSellerEligible) {
      logSupportChatDebug('not loading provider', {
        reason: isFeatureLoading ? 'feature_loading' : 'seller_not_eligible',
      })
      clearSupportChatProvider(provider)
      loadedProviderSignatureRef.current = null
      return
    }

    if (loadedProviderSignatureRef.current === providerSignature) {
      return
    }

    supportChatLogger.info('support_chat_provider_selected', {
      provider: providerName,
      seller_status: seller.status,
    })

    registerSupportChatProvider(provider)
    logSupportChatDebug('loading provider', { provider: providerName })
    loadedProviderSignatureRef.current = providerSignature
    void provider.load()

    return () => {
      clearSupportChatProvider(provider)
    }
  }, [
    featureToggle,
    isFeatureLoading,
    isSellerEligible,
    provider,
    providerName,
    providerSignature,
    seller.id,
    seller.status,
  ])

  return null
}

export default SupportChat
